import React from 'react';
import DefaultPageHeader from '../../shared/DefaultPageHeader/DefaultPageHeader';
import { IReplaysContext, useReplaysContext } from './ReplaysContextProvider';
import ReplaysPastSessionsWrapper from './ReplaysPastSessionsWrapper';
import ReplaysUpcomingSessionsWrapper from './ReplaysUpcomingSessionsWrapper';
import useSearchReplays from '../../../hooks/use-search-replays';
import { ReplaysListResponseType } from '../../../lib/api';
import { getEventOffsetGMT, TimeZoneDisplayTypeLabels } from '../../../lib/helpers/dateHelper';
import { utcToZonedTime } from 'date-fns-tz';
import { useTemplateProcessor } from '../../../hooks/use-template-processor';
import { SearchResultsStatus } from '../../../components/search-results-status/search-results-status';

interface IProps {
  label?: string;
}

const ReplaysDetails: React.FC<IProps> = (props: IProps): JSX.Element | null => {
  const { label = '' } = props;

  const {
    eventData,
    pageConfiguration,
    upcomingSessions,
    pastSessions,
    isValidatingUpcomingSessions = true,
    isValidatingPastSessions = true
  } = useReplaysContext() as IReplaysContext;
  const startDateTime = utcToZonedTime(eventData.startTime, eventData.timeZone);
  const templateProcessor = useTemplateProcessor();
  const timeZoneLabel = getEventOffsetGMT(
    eventData.timeZoneDisplayType as TimeZoneDisplayTypeLabels,
    eventData.customTimeZoneDisplay as string,
    startDateTime,
    eventData.timeZone
  );
  const getTransformedTimeZone = () => {
    return eventData.timeZoneDisplayType === TimeZoneDisplayTypeLabels.Custom ? templateProcessor(timeZoneLabel) : timeZoneLabel;
  };
  const transformedTimeZone = eventData.showEventTimeZone ? getTransformedTimeZone() : '';

  const {
    searchedUpcomingSessions,
    searchedPastSessions,
    handleSearch,
    searchValue,
  } = useSearchReplays(
    upcomingSessions as ReplaysListResponseType,
    pastSessions as ReplaysListResponseType
  );
  const searchResultsCount = (searchedUpcomingSessions?.length ?? 0) + (searchedPastSessions?.length ?? 0);

  if ((upcomingSessions || pastSessions) && !isValidatingUpcomingSessions && !isValidatingPastSessions) {
    return (
      <div className={'shadow-gray bg-white mx-auto container md:w-3/4 md:px-8 py-8 mt-12 max-h-full h-70 w-100 mx-0 px-0'}>
        <DefaultPageHeader
          label={label}
          notes={pageConfiguration.replaysNotes}
          dataTestId='replays-notes'
          handleSearch={handleSearch}
        />
        {
          searchValue &&
            <>
              <SearchResultsStatus
                as='section'
                className='replays-search-result'
                count={searchResultsCount}
                searchValue={searchValue}
              />
              <hr />
            </>
        }
        <div className='mt-4'>
          {pageConfiguration.upcomingSessionsEnabled &&
            <ReplaysUpcomingSessionsWrapper
              event={eventData}
              showAnchorElement={!!searchedPastSessions?.length}
              sessionLabel={pageConfiguration.upcomingSessionsLabel}
              pastSessionsLabel={pageConfiguration.replaysSessionLabel}
              upcomingSessions={searchedUpcomingSessions}
              transformedTimeZone={transformedTimeZone}
            />
          }
          <ReplaysPastSessionsWrapper
            event={eventData}
            showAnchorElement={!!searchedUpcomingSessions?.length}
            sessionLabel={pageConfiguration.replaysSessionLabel}
            upcomingSessionsLabel={pageConfiguration.upcomingSessionsLabel}
            pastSessions={searchedPastSessions}
          />
        </div>
      </div>
    );
  }
  return null;
};

export default ReplaysDetails;